import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

/* ── Colour tokens ───────────────────────────────────────────── */
const C = {
  gold:         "#C9933A",
  goldLt:       "#F8F6F2",
  goldGradient: "linear-gradient(45deg, #1A1040 0%, #C9933A 100%)",
  dark:         "#1A1040",
  muted:        "#4A4A4A",
  border:       "#e7dfd2",
  white:        "#F8F6F2",
};

/* ── Static styles ───────────────────────────────────────────── */
const S = {
  promoWrap: {
    display:        "flex",
    alignItems:     "center",
    padding:        "9px 0",
    fontSize:       16,
    fontWeight:     500,
    letterSpacing:  "0.04em",
    fontFamily:     "'DM Sans', sans-serif",
    textDecoration: "none",
    overflow:       "hidden",
    whiteSpace:     "nowrap",
    position:       "relative",
    width:          "100%",
    background:     "linear-gradient(45deg, #1A1040 0%, #C9933A 100%)",
    color:          "#F8F6F2",
  },
  promoCode: {
    fontWeight:   700,
    background:   "rgba(255,255,255,0.22)",
    padding:      "2px 8px",
    borderRadius: 3,
  },
  header: {
    background:   C.white,
    position:     "sticky",
    top:          0,
    zIndex:       999,
    width:        "100%",
    maxWidth:     "100%",
    boxSizing:    "border-box",
    borderBottom: `1px solid ${C.border}`,
    transition:   "box-shadow .28s",
  },
  headerStick: { boxShadow: "0 4px 28px rgba(0,0,0,0.08)" },
  mainRow: {
    display:        "flex",
    alignItems:     "center",
    justifyContent: "space-between",
    padding:        "0 40px",
    height:         78,
    position:       "relative",
    width:          "100%",
    maxWidth:       "100%",
    boxSizing:      "border-box",
  },
  navLeft: {
    display:        "flex",
    alignItems:     "center",
    flex:           1,
    justifyContent: "center",
  },
  navItem: {
    position:      "relative",
    display:       "flex",
    alignItems:    "center",
    gap:           5,
    padding:       "0 18px",
    height:        78,
    fontSize:      13,
    fontWeight:    500,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color:         C.dark,
    textDecoration:"none",
    border:        "none",
    background:    "none",
    cursor:        "pointer",
    whiteSpace:    "nowrap",
    fontFamily:    "'DM Sans', sans-serif",
    transition:    "color .2s",
  },
  logoWrap: {
    display:        "flex",
    flexDirection:  "row",
    alignItems:     "center",
    justifyContent: "center",
    gap:            10,
    textDecoration: "none",
    flexShrink:     0,
    padding:        "0 30px 0 0",
  },
  logoMain: {
    fontFamily:           "'Cormorant Garamond', serif",
    fontSize:             30,
    fontWeight:           800,
    letterSpacing:        "0.03em",
    lineHeight:           1,
    background:           C.goldGradient,
    WebkitBackgroundClip: "text",
    WebkitTextFillColor:  "transparent",
    backgroundClip:       "text",
  },
  logoSub: {
    fontFamily:    "'DM Sans', sans-serif",
    fontSize:      10,
    letterSpacing: "0.25em",
    color:         C.muted,
    textTransform: "uppercase",
    fontWeight:    400,
    marginTop:     4,
  },
  iconsRight: {
    display:        "flex",
    alignItems:     "center",
    gap:            4,
    flexShrink:     0,
    justifyContent: "flex-end",
  },
  iconBtn: {
    position:       "relative",
    width:          40,
    height:         40,
    display:        "flex",
    alignItems:     "center",
    justifyContent: "center",
    border:         "none",
    background:     "none",
    color:          C.dark,
    cursor:         "pointer",
    borderRadius:   "50%",
    transition:     "color .2s, background .2s",
  },
  badge: {
    position:       "absolute",
    top:            4,
    right:          4,
    width:          16,
    height:         16,
    borderRadius:   "50%",
    background:     C.goldGradient,
    color:          C.white,
    fontSize:       9,
    fontWeight:     700,
    display:        "flex",
    alignItems:     "center",
    justifyContent: "center",
    pointerEvents:  "none",
  },
  dropdown: {
    position:     "absolute",
    top:          "100%",
    left:         0,
    background:   C.white,
    border:       `1px solid ${C.border}`,
    borderTop:    `2px solid ${C.gold}`,
    borderRadius: 4,
    minWidth:     220,
    padding:      "8px 0",
    zIndex:       9999,
    boxShadow:    "0 12px 32px rgba(0,0,0,0.09)",
  },
  accDropdown: {
    position:     "absolute",
    top:          "calc(100% + 6px)",
    right:        0,
    background:   C.white,
    border:       `1px solid ${C.border}`,
    borderTop:    `2px solid ${C.gold}`,
    borderRadius: 4,
    minWidth:     190,
    padding:      "8px 0",
    zIndex:       9999,
    boxShadow:    "0 12px 32px rgba(0,0,0,0.09)",
  },
  dropItem: {
    display:        "flex",
    alignItems:     "center",
    gap:            10,
    width:          "100%",
    textAlign:      "left",
    fontSize:       13,
    padding:        "10px 20px",
    color:          C.dark,
    background:     "none",
    border:         "none",
    cursor:         "pointer",
    fontFamily:     "'DM Sans', sans-serif",
    fontWeight:     400,
    letterSpacing:  "0.03em",
    textDecoration: "none",
    whiteSpace:     "nowrap",
    transition:     "background .15s, color .15s",
  },
  dropItemImg: {
    width:        28,
    height:       28,
    borderRadius: 4,
    objectFit:    "cover",
    flexShrink:   0,
    border:       `1px solid ${C.border}`,
  },
  dropItemImgPlaceholder: {
    width:        28,
    height:       28,
    borderRadius: 4,
    background:   C.goldLt,
    flexShrink:   0,
    border:       `1px solid ${C.border}`,
  },
  dropLoading: {
    padding:    "12px 20px",
    fontSize:   12,
    color:      C.muted,
    fontFamily: "'DM Sans', sans-serif",
    fontStyle:  "italic",
  },
  dropError: {
    padding:    "12px 20px",
    fontSize:   12,
    color:      "#c0392b",
    fontFamily: "'DM Sans', sans-serif",
  },
  searchOverlay: {
    position:    "absolute",
    top:         "100%",
    left:        0,
    right:       0,
    background:  C.white,
    borderBottom:`2px solid ${C.gold}`,
    boxShadow:   "0 12px 32px rgba(26,16,64,0.10)",
    zIndex:      9998,
    overflow:    "hidden",
    transition:  "max-height 0.32s cubic-bezier(0.4,0,0.2,1), opacity 0.28s ease",
  },
  searchOverlayInner: {
    display:    "flex",
    alignItems: "center",
    padding:    "14px 40px",
    gap:        12,
  },
  searchIconInBar: {
    color:      C.gold,
    flexShrink: 0,
    display:    "flex",
    alignItems: "center",
  },
  searchInput: {
    flex:          1,
    padding:       "10px 0",
    fontSize:      15,
    border:        "none",
    borderBottom:  `1.5px solid ${C.border}`,
    background:    "transparent",
    fontFamily:    "'DM Sans', sans-serif",
    color:         C.dark,
    outline:       "none",
    letterSpacing: "0.03em",
    transition:    "border-color .2s",
  },
  searchBtn: {
    display:        "flex",
    alignItems:     "center",
    gap:            7,
    padding:        "8px 20px",
    background:     C.goldGradient,
    color:          C.white,
    border:         "none",
    borderRadius:   4,
    fontFamily:     "'DM Sans', sans-serif",
    fontSize:       13,
    fontWeight:     600,
    letterSpacing:  "0.07em",
    textTransform:  "uppercase",
    cursor:         "pointer",
    flexShrink:     0,
    transition:     "opacity .2s",
    whiteSpace:     "nowrap",
  },
  searchCloseBtn: {
    width:          34,
    height:         34,
    display:        "flex",
    alignItems:     "center",
    justifyContent: "center",
    border:         "none",
    background:     "none",
    color:          C.muted,
    cursor:         "pointer",
    borderRadius:   "50%",
    flexShrink:     0,
    transition:     "color .2s, background .2s",
  },
  dropIcon: {
    width:          28,
    height:         28,
    borderRadius:   6,
    display:        "flex",
    alignItems:     "center",
    justifyContent: "center",
    flexShrink:     0,
    background:     C.goldLt,
    color:          C.dark,
    transition:     "background .15s, color .15s",
  },
  mobileMenuOverlay: {
    position:   "fixed",
    top:        0,
    left:       0,
    right:      0,
    bottom:     0,
    background: "rgba(0,0,0,0.5)",
    zIndex:     10000,
    opacity:    0,
    visibility: "hidden",
    transition: "opacity 0.3s ease, visibility 0.3s ease",
  },
  mobileMenu: {
    position:   "fixed",
    top:        0,
    left:       0,
    bottom:     0,
    width:      "85%",
    maxWidth:   320,
    background: C.white,
    zIndex:     10001,
    transform:  "translateX(-100%)",
    transition: "transform 0.3s ease",
    overflowY:  "auto",
    boxShadow:  "2px 0 20px rgba(0,0,0,0.1)",
  },
  mobileMenuHeader: {
    display:        "flex",
    alignItems:     "center",
    justifyContent: "space-between",
    padding:        "20px",
    borderBottom:   `1px solid ${C.border}`,
  },
  mobileMenuLogo: {
    fontFamily:           "'Cormorant Garamond', serif",
    fontSize:             20,
    fontWeight:           800,
    background:           C.goldGradient,
    WebkitBackgroundClip: "text",
    WebkitTextFillColor:  "transparent",
    backgroundClip:       "text",
  },
  mobileCloseBtn: {
    width:          36,
    height:         36,
    display:        "flex",
    alignItems:     "center",
    justifyContent: "center",
    border:         "none",
    background:     "none",
    cursor:         "pointer",
    borderRadius:   "50%",
    transition:     "background 0.2s",
  },
  mobileNavItem: {
    display:        "flex",
    alignItems:     "center",
    justifyContent: "space-between",
    padding:        "16px 20px",
    fontSize:       15,
    fontWeight:     500,
    color:          C.dark,
    textDecoration: "none",
    borderBottom:   `1px solid ${C.border}`,
    transition:     "background 0.2s, color 0.2s",
    fontFamily:     "'DM Sans', sans-serif",
    cursor:         "pointer",
  },
  mobileSubMenu: {
    background:  C.goldLt,
    paddingLeft: 20,
  },
  mobileCategoryItem: {
    display:        "flex",
    alignItems:     "center",
    gap:            12,
    padding:        "12px 20px",
    fontSize:       14,
    color:          C.dark,
    textDecoration: "none",
    borderBottom:   `1px solid ${C.border}`,
    transition:     "background 0.2s",
    cursor:         "pointer",
  },
  mobileAccountSection: {
    padding:   "20px",
    borderTop: `1px solid ${C.border}`,
    marginTop: 20,
  },
  mobileAccountBtn: {
    display:        "flex",
    alignItems:     "center",
    gap:            12,
    width:          "100%",
    padding:        "12px 16px",
    background:     C.goldGradient,
    color:          C.white,
    border:         "none",
    borderRadius:   8,
    fontSize:       14,
    fontWeight:     600,
    cursor:         "pointer",
    fontFamily:     "'DM Sans', sans-serif",
    textDecoration: "none",
    justifyContent: "center",
  },
  mobileWhatsApp: {
    display:        "flex",
    alignItems:     "center",
    gap:            10,
    padding:        "12px 20px",
    color:          C.dark,
    textDecoration: "none",
    fontSize:       14,
    fontFamily:     "'DM Sans', sans-serif",
  },
};

const API_BASE = process.env.REACT_APP_API_URL;

/* ── Custom hook: fetch categories ──────────────────────────── */
const useCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(`${API_BASE}/categories`)
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((d)  => { if (!cancelled) { setCategories(Array.isArray(d) ? d : []); setLoading(false); } })
      .catch((e) => { if (!cancelled) { setError(e.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, []);

  return { categories, loading, error };
};

/* ── Custom hook: fetch active promo bar ─────────────────────── */
const usePromoBar = () => {
  const [message, setMessage] = useState(null);
  const [loaded,  setLoaded]  = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/promo-bar/active`)
      .then((r) => { if (!r.ok) throw new Error(); return r.json(); })
      .then((d)  => { if (!cancelled) { setMessage(d?.message || null); setLoaded(true); } })
      .catch(()  => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, []);

  return { message, loaded };
};

const MsgDot = () => (
  <span style={{
    display:        "inline-flex",
    alignItems:     "center",
    justifyContent: "center",
    margin:         "0 40px",
    verticalAlign:  "middle",
    flexShrink:     0,
    fontSize:       12,
    color:          "rgba(255,255,255,0.85)",
    lineHeight:     1,
  }}>✦</span>
);

/* ── Seamless looping marquee ────────────────────────────────── */
const PromoStrip = () => {
  const { message, loaded } = usePromoBar();
  if (!loaded || !message) return null;

  const messages = message.includes("|")
    ? message.split("|").map((m) => m.trim()).filter(Boolean)
    : [message.trim()];

  const TrackContent = () => (
    <>
      {messages.map((msg, i) => (
        <span key={i} style={{ display: "inline-flex", alignItems: "center" }}>
          <span className="emb-marquee-item">{msg}</span>
          <MsgDot />
        </span>
      ))}
    </>
  );

  return (
    <div className="emb-promo-wrap emb-marquee-wrap" style={S.promoWrap}>
      <div className="emb-marquee-track" aria-hidden="false">
        <TrackContent />
      </div>
      <div className="emb-marquee-track" aria-hidden="true">
        <TrackContent />
      </div>
    </div>
  );
};

/* ── SVG Icons ───────────────────────────────────────────────── */
const IconSearch   = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"><circle cx="11" cy="11" r="7"/><line x1="16.5" y1="16.5" x2="22" y2="22"/></svg>;
const IconUser     = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>;
const IconHeart    = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M20.8 4.6a5.5 5.5 0 00-7.8 0L12 5.6l-1-1a5.5 5.5 0 00-7.8 7.8l1 1L12 21l7.8-7.8 1-1a5.5 5.5 0 000-7.6z"/></svg>;
const IconCart     = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/></svg>;
const IconMenu     = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>;
const IconClose    = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>;
const ChevronRight = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>;

const IconMyAccount = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
  </svg>
);
const IconMyOrders = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2"/>
    <rect x="9" y="3" width="6" height="4" rx="1"/>
    <line x1="9" y1="12" x2="15" y2="12"/>
    <line x1="9" y1="16" x2="13" y2="16"/>
  </svg>
);
const IconLogout = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/>
    <polyline points="16 17 21 12 16 7"/>
    <line x1="21" y1="12" x2="9" y2="12"/>
  </svg>
);
const IconLogin = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M15 3h4a2 2 0 012 2v14a2 2 0 01-2 2h-4"/>
    <polyline points="10 17 15 12 10 7"/>
    <line x1="15" y1="12" x2="3" y2="12"/>
  </svg>
);

const ChevronDown = ({ open }) => (
  <svg width="9" height="9" viewBox="0 0 10 6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"
    style={{ transition: "transform .2s", transform: open ? "rotate(180deg)" : "none" }}>
    <path d="M1 1l4 4 4-4"/>
  </svg>
);

/* ── Hover helpers ───────────────────────────────────────────── */
const iconHover = (e) => { e.currentTarget.style.color = C.gold;  e.currentTarget.style.background = C.goldLt; };
const iconOut   = (e) => { e.currentTarget.style.color = C.dark;  e.currentTarget.style.background = "none"; };
const dropHover = (e) => { e.currentTarget.style.background = C.goldLt; e.currentTarget.style.color = C.gold; };
const dropOut   = (e) => { e.currentTarget.style.background = "none";   e.currentTarget.style.color = C.dark; };

/* ── Category dropdown item ──────────────────────────────────── */
const CategoryDropItem = ({ category, onClick }) => {
  const imgSrc = category.image ? `${API_BASE}/${category.image}` : null;
  return (
    <button className="emb-drop-item" style={S.dropItem} onMouseEnter={dropHover} onMouseLeave={dropOut} onClick={onClick}>
      {imgSrc
        ? <img src={imgSrc} alt={category.name} style={S.dropItemImg} onError={(e) => { e.currentTarget.style.display = "none"; }} />
        : <div style={S.dropItemImgPlaceholder} />
      }
      <span>{category.name}</span>
    </button>
  );
};

/* ── Nav item with optional dropdown ────────────────────────── */
const NavItem = ({ label, to, children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [dropOpen, setDropOpen] = useState(false);
  const timerRef = useRef(null);
  const normalizePath = (path) => path === '/' ? '/' : (path?.replace(/\/+$/, '') || '/');
  const currentPath = normalizePath(location.pathname);
  const targetPath = to ? normalizePath(to) : '';
  const hasCategoryFilter = currentPath === '/shop' && Boolean(new URLSearchParams(location.search).get('category'));
  const isCollectionActive = !to && label === 'Collection' && hasCategoryFilter;
  const isShopActive = label === 'Shop' && currentPath === '/shop' && !hasCategoryFilter;
  const isActive = Boolean(isCollectionActive || isShopActive || (to && label !== 'Shop' && (currentPath === targetPath || (targetPath !== '/' && currentPath.startsWith(`${targetPath}/`)))));

  return (
    <div
      style={{ position: "relative" }}
      onMouseEnter={() => { clearTimeout(timerRef.current); setDropOpen(true); }}
      onMouseLeave={() => { timerRef.current = setTimeout(() => setDropOpen(false), 150); }}
    >
      <button
        className={`emb-nav-item${isActive ? ' is-active' : ''}`}
        style={{ ...S.navItem, color: isActive ? C.gold : C.dark, boxShadow: isActive ? `inset 0 -3px ${C.gold}` : 'none' }}
        onClick={() => to && navigate(to)}
        onMouseEnter={(e) => { if (to) e.currentTarget.style.color = C.gold; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = isActive ? C.gold : C.dark; }}
      >
        {label}
        {children && <ChevronDown open={dropOpen} />}
      </button>
      {children && dropOpen && <div className="emb-dropdown" style={S.dropdown}>{children}</div>}
    </div>
  );
};

/* ── Mobile Menu ─────────────────────────────────────────────── */
const MobileMenu = ({ isOpen, onClose, categories, catLoading, catError, isLoggedIn, onLogout, navigate, openWishlistDrawer, openCartDrawer }) => {
  const [collectionOpen, setCollectionOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e) => { if (e.key === "Escape" && isOpen) onClose(); };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  const handleLinkClick = (path) => { navigate(path); onClose(); };
  const categoryPath    = (name) => `/shop?category=${encodeURIComponent(name)}`;

  return (
    <>
      <div
        style={{ ...S.mobileMenuOverlay, opacity: isOpen ? 1 : 0, visibility: isOpen ? "visible" : "hidden" }}
        onClick={onClose}
      />
      <div ref={menuRef} style={{ ...S.mobileMenu, transform: isOpen ? "translateX(0)" : "translateX(-100%)" }}>

        <div style={S.mobileMenuHeader}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <img src={`${process.env.PUBLIC_URL}/sriram-logo.png`} alt="SriRam" style={{ width: 48, height: 48, objectFit: "contain", display: "block" }} />
            <div>
              <div className="emb-logo-main" style={S.logoMain}>EMARTEBUY</div>
              <div className="emb-logo-sub" style={S.logoSub}>where tradition meets elegance</div>
            </div>
          </div>
          <button style={S.mobileCloseBtn} onClick={onClose}
            onMouseEnter={(e) => { e.currentTarget.style.background = C.goldLt; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "none"; }}>
            <IconClose />
          </button>
        </div>

        <div>
          <div style={S.mobileNavItem} onClick={() => handleLinkClick("/")}>
            <span>Home</span>
          </div>
          <div style={S.mobileNavItem} onClick={() => handleLinkClick("/about")}>
            <span>About</span>
          </div>
          <div style={S.mobileNavItem} onClick={() => handleLinkClick("/shop")}>
            <span>Shop</span>
          </div>

          <div>
            <div style={S.mobileNavItem} onClick={() => setCollectionOpen(!collectionOpen)}>
              <span>Collection</span>
              <ChevronDown open={collectionOpen} />
            </div>
            {collectionOpen && (
              <div style={S.mobileSubMenu}>
                {catLoading && <div style={S.dropLoading}>Loading categories…</div>}
                {catError   && <div style={S.dropError}>Could not load categories.</div>}
                {!catLoading && !catError && categories.length === 0 && (
                  <div style={S.dropLoading}>No categories found.</div>
                )}
                {!catLoading && categories.map((cat) => (
                  <div key={cat.id} style={S.mobileCategoryItem} onClick={() => handleLinkClick(categoryPath(cat.name))}>
                    {cat.image && (
                      <img src={`${API_BASE}/${cat.image}`} alt={cat.name}
                        style={{ width: 28, height: 28, borderRadius: 4, objectFit: "cover" }}
                        onError={(e) => { e.currentTarget.style.display = "none"; }} />
                    )}
                    <span>{cat.name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={S.mobileNavItem} onClick={() => handleLinkClick("/contact")}>
            <span>Contact</span>
          </div>
          <div style={S.mobileNavItem} onClick={() => { openWishlistDrawer(); onClose(); }}>
            <span>Wishlist</span>
          </div>
          <div style={S.mobileNavItem} onClick={() => { openCartDrawer(); onClose(); }}>
            <span>Cart</span>
          </div>
        </div>

        <div style={S.mobileAccountSection}>
          {isLoggedIn ? (
            <>
              <div style={S.mobileCategoryItem} onClick={() => handleLinkClick("/my-account")}>
                <IconMyAccount /><span>My Account</span>
              </div>
              <div style={S.mobileCategoryItem} onClick={() => handleLinkClick("/my-orders")}>
                <IconMyOrders /><span>My Orders</span>
              </div>
              <button
                style={{ ...S.mobileAccountBtn, background: "#fdf0f0", color: "#c0392b", marginTop: 10 }}
                onClick={() => { onLogout(); onClose(); }}
              >
                <IconLogout /> Logout
              </button>
            </>
          ) : (
            <button style={S.mobileAccountBtn} onClick={() => handleLinkClick("/login-register")}>
              <IconLogin /> Login / Register
            </button>
          )}
        </div>
      </div>
    </>
  );
};

/* ══════════════════════════════════════════════════════════════
   Main Component
══════════════════════════════════════════════════════════════ */
const HeaderOne = ({ shop }) => {
  const [scroll,         setScroll]         = useState(0);
  const [accOpen,        setAccOpen]        = useState(false);
  const [searchOpen,     setSearchOpen]     = useState(false);
  const [searchInput,    setSearchInput]    = useState("");
  const [isLoggedIn,     setIsLoggedIn]     = useState(() => !!localStorage.getItem("isLoggedIn"));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const accCloseTimer    = useRef(null);
  const searchOverlayRef = useRef(null);
  const searchInputRef   = useRef(null);
  const navigate = useNavigate();

  const { categories, loading: catLoading, error: catError } = useCategories();
  const { wishlistItems, cartItems, openCartDrawer, openWishlistDrawer } = shop;

  useEffect(() => {
    const fn = () => setScroll(window.scrollY);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  useEffect(() => {
    if (searchOpen) setTimeout(() => searchInputRef.current?.focus(), 40);
    else setSearchInput("");
  }, [searchOpen]);

  useEffect(() => {
    if (!searchOpen) return;
    const handler = (e) => {
      if (searchOverlayRef.current && !searchOverlayRef.current.contains(e.target))
        setSearchOpen(false);
    };
    const id = setTimeout(() => document.addEventListener("mousedown", handler), 20);
    return () => { clearTimeout(id); document.removeEventListener("mousedown", handler); };
  }, [searchOpen]);

  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") setSearchOpen(false); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  useEffect(() => {
    const sync = () => setIsLoggedIn(!!localStorage.getItem("isLoggedIn"));
    window.addEventListener("authChange", sync);
    window.addEventListener("storage",    sync);
    return () => { window.removeEventListener("authChange", sync); window.removeEventListener("storage", sync); };
  }, []);

  const handleSearch = () => {
    const q = searchInput.trim();
    if (!q) return;
    setSearchOpen(false);
    navigate(`/shop?q=${encodeURIComponent(q)}`);
  };

  const handleKeyDown = (e) => { if (e.key === "Enter") handleSearch(); };

  const handleLogout = () => {
    ["isLoggedIn","user","customer","customerId","cart","wishlist","cartOwnerId","wishlistOwnerId"]
      .forEach((k) => localStorage.removeItem(k));
    setIsLoggedIn(false);
    window.dispatchEvent(new Event("authChange"));
    window.dispatchEvent(new Event("cartUpdated"));
    window.dispatchEvent(new Event("wishlistUpdated"));
    navigate("/");
  };

  const categoryPath = (name) => `/shop?category=${encodeURIComponent(name)}`;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@600;700;800&family=DM+Sans:wght@300;400;500;600&display=swap');

        .emb-header { width: 100% !important; max-width: 100% !important; box-sizing: border-box !important; left: 0; right: 0; }
        .emb-search-overlay { max-height: 0; opacity: 0; pointer-events: none; }
        .emb-search-overlay.open { max-height: 100px; opacity: 1; pointer-events: auto; }
        .emb-search-input:focus { border-bottom-color: ${C.gold} !important; }
        .emb-search-icon-btn.active { color: ${C.gold} !important; background: ${C.goldLt} !important; }
        .emb-search-submit:hover { opacity: 0.85; }
        .emb-search-close:hover { color: ${C.dark} !important; background: ${C.goldLt} !important; }

        .emb-acc-item:hover { background: ${C.goldLt} !important; color: ${C.gold} !important; }
        .emb-acc-item:hover .emb-acc-icon { background: ${C.gold} !important; color: #fff !important; }
        .emb-acc-logout:hover { background: #fdf0f0 !important; color: #c0392b !important; }
        .emb-acc-logout:hover .emb-acc-icon-danger { background: #c0392b !important; color: #fff !important; }

        .emb-promo-wrap {
          display: flex;
          overflow: hidden;
          width: 100%;
        }

        @keyframes emb-marquee {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-100%); }
        }

        .emb-marquee-track {
          display: inline-flex;
          align-items: center;
          white-space: nowrap;
          flex-shrink: 0;
          min-width: 100%;
          animation: emb-marquee 22s linear infinite;
        }

        .emb-marquee-wrap:hover .emb-marquee-track {
          animation-play-state: paused;
        }

        .emb-marquee-item {
          display: inline-flex;
          align-items: center;
          white-space: nowrap;
          padding: 0 20px;
          font-family: 'DM Sans', sans-serif;
        }

        @media (max-width: 1199px) and (min-width: 992px) {
          .emb-main-row  { padding: 0 20px !important; }
          .emb-nav-item  { padding: 0 12px !important; font-size: 12px !important; }
          .emb-logo-wrap { padding: 0 15px 0 0 !important; }
          .emb-logo-icon { width: 48px !important; height: 48px !important; }
          .emb-logo-main { font-size: 24px !important; }
          .emb-logo-sub  { font-size: 8px !important; letter-spacing: 0.16em !important; }
        }
        @media (max-width: 991px) {
          .emb-nav-left  { display: none !important; }
          .emb-main-row  { padding: 0 16px !important; height: 70px !important; justify-content: space-between !important; }
          .emb-logo-wrap { padding: 0 !important; position: static !important; transform: none !important; align-items: center !important; text-align: left !important; }
          .emb-logo-icon { width: 46px !important; height: 46px !important; }
          .emb-logo-main { font-size: 22px !important; }
          .emb-logo-sub  { font-size: 8px !important; letter-spacing: 0.15em !important; margin-top: 3px !important; }
          .emb-icons-right { gap: 0 !important; }
          .emb-icon-btn  { width: 36px !important; height: 36px !important; }
          .emb-badge     { width: 14px !important; height: 14px !important; font-size: 8px !important; top: 2px !important; right: 2px !important; }
          .emb-search-overlay-inner { padding: 12px 16px !important; }
          .emb-search-input  { font-size: 14px !important; }
          .emb-search-submit { padding: 8px 16px !important; }
          .emb-search-submit-label { display: none !important; }
          .emb-search-close  { width: 32px !important; height: 32px !important; }
          .emb-promo-wrap    { font-size: 13px !important; }
          .emb-marquee-item  { padding: 0 20px !important; }
        }
        @media (max-width: 767px) {
          .emb-main-row  { padding: 0 12px !important; height: 64px !important; }
          .emb-logo-wrap { position: static !important; transform: none !important; align-items: center !important; }
          .emb-logo-icon { width: 42px !important; height: 42px !important; }
          .emb-logo-main { font-size: 20px !important; }
          .emb-logo-sub  { font-size: 7px !important; letter-spacing: 0.12em !important; }
          .emb-icon-btn  { width: 32px !important; height: 32px !important; }
          .emb-icon-btn svg { width: 16px !important; height: 16px !important; }
          .emb-badge     { width: 13px !important; height: 13px !important; font-size: 7px !important; }
          .emb-promo-wrap   { font-size: 12px !important; }
          .emb-marquee-item { padding: 0 16px !important; gap: 6px !important; }
          .emb-search-overlay.open { max-height: 80px !important; }
          .emb-search-overlay-inner { padding: 10px 12px !important; gap: 8px !important; }
          .emb-search-input  { font-size: 13px !important; padding: 6px 0 !important; }
          .emb-search-submit { padding: 6px 12px !important; }
          .emb-search-close  { width: 28px !important; height: 28px !important; }
        }
        @media (max-width: 575px) {
          .emb-main-row  { padding: 0 10px !important; height: 58px !important; }
          .emb-logo-wrap { position: static !important; transform: none !important; align-items: center !important; }
          .emb-logo-icon { width: 36px !important; height: 36px !important; }
          .emb-logo-main { font-size: 17px !important; }
          .emb-logo-sub  { font-size: 5.5px !important; letter-spacing: 0.08em !important; margin-top: 1px !important; }
          .emb-icon-btn  { width: 30px !important; height: 30px !important; }
          .emb-icon-btn svg { width: 14px !important; height: 14px !important; }
          .emb-badge     { width: 12px !important; height: 12px !important; font-size: 6.5px !important; top: 1px !important; right: 1px !important; }
          .emb-promo-wrap   { font-size: 11px !important; }
          .emb-marquee-item { padding: 0 12px !important; gap: 4px !important; }
          .emb-search-overlay.open { max-height: 70px !important; }
          .emb-search-overlay-inner { padding: 8px 10px !important; gap: 6px !important; }
          .emb-search-input  { font-size: 12px !important; padding: 5px 0 !important; }
          .emb-search-submit { padding: 5px 10px !important; }
          .emb-search-close  { width: 26px !important; height: 26px !important; }
          .emb-search-close svg { width: 12px !important; height: 12px !important; }
        }
        @media (max-width: 768px) and (orientation: landscape) {
          .emb-main-row  { height: 54px !important; }
          .emb-nav-left  { display: none !important; }
          .emb-logo-main { font-size: 18px !important; }
          .emb-logo-sub  { font-size: 6px !important; }
          .emb-promo-wrap { font-size: 10px !important; }
        }
        @media (min-width: 1024px) and (max-width: 1366px) and (orientation: landscape) {
          .emb-main-row { padding: 0 30px !important; }
          .emb-nav-item { padding: 0 14px !important; font-size: 12px !important; }
          .emb-logo-main { font-size: 26px !important; }
        }
        @media (min-width: 1920px) {
          .emb-main-row  { padding: 0 60px !important; height: 90px !important; }
          .emb-nav-item  { padding: 0 24px !important; font-size: 14px !important; }
          .emb-logo-main { font-size: 36px !important; }
          .emb-logo-sub  { font-size: 11px !important; letter-spacing: 0.3em !important; }
          .emb-icon-btn  { width: 45px !important; height: 45px !important; }
          .emb-icon-btn svg { width: 20px !important; height: 20px !important; }
        }
        @media print {
          .emb-header, .emb-promo-wrap, .emb-icons-right, .emb-search-overlay { display: none !important; }
        }
        @media (prefers-reduced-motion: reduce) {
          .emb-header, .emb-promo-wrap, .emb-icon-btn, .emb-dropdown,
          .emb-search-overlay, .emb-marquee-track {
            transition: none !important; animation: none !important;
          }
        }
        @media (max-width: 991px) { .d-lg-none { display: flex !important; } }
        @media (min-width: 992px) {
          .d-lg-none  { display: none !important; }
          .d-lg-flex  { display: flex !important; }
          .d-lg-block { display: block !important; }
        }
      `}</style>

      {/* ── Promo Bar ── */}
      <PromoStrip />

      {/* ── Sticky Header ── */}
      <header className="emb-header" style={{ ...S.header, ...(scroll > 0 ? S.headerStick : {}) }}>
        <div className="emb-main-row" style={S.mainRow}>

          {/* LEFT LOGO */}
          <Link to="/" className="emb-logo-wrap" style={S.logoWrap}>
          <img className="emb-logo-icon" src={`${process.env.PUBLIC_URL}/sriram-logo.png`} alt="SriRam" style={{ width: 64, height: 64, objectFit: "contain", display: "block" }} />
          <div className="emb-logo-wordmark">
            <div className="emb-logo-main" style={S.logoMain}>EMARTEBUY</div>
            <div className="emb-logo-sub" style={S.logoSub}>where tradition meets elegance</div>
          </div>
          </Link>

          {/* CENTER / DESKTOP NAV */}
          <nav className="emb-nav-left d-none d-lg-flex" style={S.navLeft} aria-label="Main navigation">
            <NavItem label="Home"       to="/" />
            <NavItem label="About"      to="/about" />
            <NavItem label="Shop"       to="/shop" />
            <NavItem label="Collection">
              {catLoading && <div style={S.dropLoading}>Loading categories…</div>}
              {catError   && <div style={S.dropError}>Could not load categories.</div>}
              {!catLoading && !catError && categories.length === 0 && (
                <div style={S.dropLoading}>No categories found.</div>
              )}
              {!catLoading && categories.map((cat) => (
                <CategoryDropItem key={cat.id} category={cat} onClick={() => navigate(categoryPath(cat.name))} />
              ))}
            </NavItem>
            <NavItem label="Contact" to="/contact" />
          </nav>

          {/* RIGHT ICONS */}
          <div className="emb-icons-right" style={S.iconsRight}>

            <button
              className={`emb-icon-btn emb-search-icon-btn${searchOpen ? " active" : ""}`}
              style={{ ...S.iconBtn, color: searchOpen ? C.gold : C.dark, background: searchOpen ? C.goldLt : "none" }}
              onClick={() => setSearchOpen((v) => !v)}
              aria-label="Search" aria-expanded={searchOpen}
            >
              <IconSearch />
            </button>

            <button className="emb-icon-btn" style={S.iconBtn} onMouseEnter={iconHover} onMouseLeave={iconOut} onClick={openWishlistDrawer} aria-label="Wishlist">
              <IconHeart />
              {wishlistItems?.length > 0 && <span className="emb-badge" style={S.badge}>{wishlistItems.length}</span>}
            </button>

            <button className="emb-icon-btn" style={S.iconBtn} onMouseEnter={iconHover} onMouseLeave={iconOut} onClick={openCartDrawer} aria-label="Cart">
              <IconCart />
              {cartItems?.length > 0 && <span className="emb-badge" style={S.badge}>{cartItems.length}</span>}
            </button>

            {/* Account — desktop only */}
            <div className="d-none d-lg-block" style={{ position: "relative" }}
              onMouseEnter={() => { clearTimeout(accCloseTimer.current); setAccOpen(true); }}
              onMouseLeave={() => { accCloseTimer.current = setTimeout(() => setAccOpen(false), 150); }}>
              <button className="emb-icon-btn" style={S.iconBtn} onMouseEnter={iconHover} onMouseLeave={iconOut} aria-label="Account">
                <IconUser />
              </button>
              {accOpen && (
                <div className="emb-acc-dropdown" style={S.accDropdown}>
                  {isLoggedIn ? (
                    <>
                      <Link to="/my-account" className="emb-acc-item" style={S.dropItem}>
                        <span className="emb-acc-icon" style={S.dropIcon}><IconMyAccount /></span>My Account
                      </Link>
                      <Link to="/my-orders" className="emb-acc-item" style={S.dropItem}>
                        <span className="emb-acc-icon" style={S.dropIcon}><IconMyOrders /></span>My Orders
                      </Link>
                      <button onClick={handleLogout} className="emb-acc-logout" style={{ ...S.dropItem, color: "#c0392b" }}>
                        <span className="emb-acc-icon-danger" style={{ ...S.dropIcon, background: "#fdf0f0", color: "#c0392b" }}>
                          <IconLogout />
                        </span>
                        Logout
                      </button>
                    </>
                  ) : (
                    <Link to="/login-register" className="emb-acc-item" style={S.dropItem}>
                      <span className="emb-acc-icon" style={S.dropIcon}><IconLogin /></span>Login / Register
                    </Link>
                  )}
                </div>
              )}
            </div>

            {/* Mobile hamburger */}
            <button className="d-block d-lg-none" style={S.iconBtn}
              onMouseEnter={iconHover} onMouseLeave={iconOut}
              onClick={() => setMobileMenuOpen(true)} aria-label="Open menu">
              <IconMenu />
            </button>

          </div>
        </div>

        {/* ── Search overlay ── */}
        <div
          ref={searchOverlayRef}
          className={`emb-search-overlay${searchOpen ? " open" : ""}`}
          style={S.searchOverlay}
          role="search" aria-hidden={!searchOpen}
        >
          <div className="emb-search-overlay-inner" style={S.searchOverlayInner}>
            <span style={S.searchIconInBar}><IconSearch /></span>
            <input
              ref={searchInputRef}
              type="text"
              className="emb-search-input"
              placeholder="Search products, categories, brands…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={handleKeyDown}
              style={S.searchInput}
              tabIndex={searchOpen ? 0 : -1}
              autoComplete="off"
            />
            <button
              className="emb-search-submit"
              style={{ ...S.searchBtn, opacity: searchInput.trim() ? 1 : 0.55, cursor: searchInput.trim() ? "pointer" : "default" }}
              onClick={handleSearch}
              disabled={!searchInput.trim()}
              aria-label="Search"
            >
              <IconSearch />
              <span className="emb-search-submit-label">Search</span>
            </button>
            <button className="emb-search-close" style={S.searchCloseBtn} onClick={() => setSearchOpen(false)} aria-label="Close search">
              <IconClose />
            </button>
          </div>
        </div>
      </header>

      {/* ── Mobile Menu ── */}
      <MobileMenu
        isOpen={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        categories={categories}
        catLoading={catLoading}
        catError={catError}
        isLoggedIn={isLoggedIn}
        onLogout={handleLogout}
        navigate={navigate}
        openWishlistDrawer={openWishlistDrawer}
        openCartDrawer={openCartDrawer}
      />
    </>
  );
};

HeaderOne.propTypes = {
  shop: PropTypes.shape({
    wishlistItems:      PropTypes.array,
    cartItems:          PropTypes.array,
    openCartDrawer:     PropTypes.func,
    openWishlistDrawer: PropTypes.func,
  }).isRequired,
};

export default HeaderOne;
